import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api, MatchSummary } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { Flag } from '../components/TeamBadge';
import { teamName } from '../lib/flags';
import { H2, Muted, BackLink } from '../components/ui';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 8px;
`;

const Stat = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px 8px;
  text-align: center;
  .n { font-size: 1.6rem; font-weight: 800; color: ${({ theme }) => theme.colors.accent}; }
  .l { font-size: 0.7rem; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const MatchRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  padding: 12px;
  margin-bottom: 8px;
  font-weight: 700;
  .sc { font-size: 1.2rem; font-weight: 800; }
`;

const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 120px;
  margin-top: 8px;
  .bar { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; }
  .fill { width: 100%; background: ${({ theme }) => theme.colors.accent}; border-radius: 4px 4px 0 0; min-height: 2px; }
  .v { font-size: 0.68rem; font-weight: 700; margin-bottom: 2px; }
  .k { font-size: 0.59rem; color: ${({ theme }) => theme.colors.textMuted}; margin-top: 4px; }
`;

function SummaryRow({ m, onClick }: { m: MatchSummary; onClick: () => void }) {
  return (
    <MatchRow onClick={onClick}>
      <Flag team={m.team1} size={20} /> {teamName(m.team1)}
      <span className="sc">
        {m.ft[0]}–{m.ft[1]}
      </span>
      {teamName(m.team2)} <Flag team={m.team2} size={20} />
    </MatchRow>
  );
}

export function Stats() {
  const navigate = useNavigate();
  const state = useAsync(() => api.stats(), [], '/stats');

  return (
    <>
      <BackLink onClick={() => navigate(-1)}>← Volver</BackLink>
      <h2 style={{ marginTop: 0 }}>Estadísticas del torneo</h2>
      <Async state={state}>
        {({ stats: s }) => {
          const maxMin = Math.max(1, ...Object.values(s.goalsByMinute));
          return (
            <>
              <Grid>
                <Stat>
                  <div className="n">{s.goals}</div>
                  <div className="l">goles</div>
                </Stat>
                <Stat>
                  <div className="n">{s.avg}</div>
                  <div className="l">por partido</div>
                </Stat>
                <Stat>
                  <div className="n">{s.played}</div>
                  <div className="l">jugados</div>
                </Stat>
                <Stat>
                  <div className="n">{s.penalties}</div>
                  <div className="l">de penal</div>
                </Stat>
                <Stat>
                  <div className="n">{s.ownGoals}</div>
                  <div className="l">en contra</div>
                </Stat>
                <Stat>
                  <div className="n">{s.scorelessDraws}</div>
                  <div className="l">empates 0-0</div>
                </Stat>
              </Grid>

              {s.biggestWin && (
                <>
                  <H2>Mayor goleada</H2>
                  <SummaryRow m={s.biggestWin} onClick={() => navigate(`/match/${s.biggestWin!.id}`)} />
                </>
              )}

              {s.mostGoals && s.mostGoals.id !== s.biggestWin?.id && (
                <>
                  <H2>Partido con más goles ({s.mostGoals.total})</H2>
                  <SummaryRow m={s.mostGoals} onClick={() => navigate(`/match/${s.mostGoals!.id}`)} />
                </>
              )}

              <H2>Hat-tricks</H2>
              {s.hatTricks.length === 0 ? (
                <Muted>Todavía no hubo.</Muted>
              ) : (
                s.hatTricks.map((h, i) => (
                  <MatchRow key={i} onClick={() => navigate(`/match/${h.matchId}`)}>
                    <Flag team={h.team} size={20} /> {h.player} — {h.goals} goles
                  </MatchRow>
                ))
              )}

              <H2>¿Cuándo se hacen los goles?</H2>
              <Bars>
                {Object.entries(s.goalsByMinute).map(([k, v]) => (
                  <div className="bar" key={k}>
                    <span className="v">{v}</span>
                    <div className="fill" style={{ height: `${(v / maxMin) * 100}%` }} />
                    <span className="k">{k}</span>
                  </div>
                ))}
              </Bars>
              <Muted style={{ marginTop: 8 }}>Minutos del partido (incluye descuento).</Muted>
            </>
          );
        }}
      </Async>
    </>
  );
}
